import { useState } from 'react'
import { simpleSuccessNotification } from 'hooks/notificationHandler'
import { useEditConcreteTestSetMutation } from 'schema'
import { DefaultMutationHookOptions } from 'utils/defaults'
import CIMSFormItem from 'components/CIMSFormItem'
import { CIMSModal } from 'components/CIMSModal'
import { useConcreteTestSetTrackerContext } from '../../ConcreteTestSetTrackerContext'
import CoreFormItems from './components/CoreFormItems'
import TestFormItems from './components/TestFormItems'
import {
  BreakDataFormItems,
  BreakDataFormItemsValue,
  BreakDataModalProps,
} from './types'
import { CoreFormItemsValue } from './components/CoreFormItems/types'
import { toMoment } from 'utils/toMoment'
import { useLabTechSelectContext } from 'components/CIMSSelect/common/LabTechSelect/LabTechSelectContext'
import { TestFormItemsValue } from './components/TestFormItems/types'
import { useBreakDateByPlacementDate } from '../../hooks/useBreakDateByPlacementDate'
import { useTestingLabSelectContext } from 'components/CIMSSelect/common/TestingLabSelect/TestingLabSelectContext'
import { errorHandler } from 'hooks/errorHandler'

const BreakDataModal: React.FC<BreakDataModalProps> = ({
  onClose,
  visible,
  state,
}) => {
  const {
    globalId,
    testSetId,
    concretePlacements = [],
    concreteTests = [],
    placementDate,
  } = state ?? {}
  const { relations } = useConcreteTestSetTrackerContext()
  const [edit] = useEditConcreteTestSetMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      editConcreteTestSet: {
        concreteTestSet: { testSetId },
      },
    }) =>
      simpleSuccessNotification(
        `Break Data for ${testSetId} successfully edited!`
      ),
  })
  const [editing, setEditing] = useState(false)
  const labTechSelectContext = useLabTechSelectContext()
  const testingLabSelectContext = useTestingLabSelectContext()
  const breakDateByPlacementDate = useBreakDateByPlacementDate(placementDate)
  const initialFormValues: BreakDataFormItems = {
    cores: concretePlacements.flatMap(({ concreteCoreMeasurements }) =>
      concreteCoreMeasurements.map<CoreFormItemsValue>(
        ({ globalId, concreteCoreBreakData }) => {
          if (!concreteCoreBreakData)
            return {
              age: 28,
              concreteCoreMeasurementId: globalId,
              breakDate: breakDateByPlacementDate()!,
              placementDate,
            }
          const { labTech, testingLab, breakDate, age, __typename, ...rest } =
            concreteCoreBreakData

          return {
            ...rest,
            concreteCoreMeasurementId: globalId,
            age: age ?? 28,
            breakDate: toMoment(breakDate),
            labTech: labTechSelectContext.toLabeledValue(labTech),
            testingLab: testingLabSelectContext.toLabeledValue(testingLab),
            placementDate,
          }
        }
      )
    ),
    tests: concreteTests.reduce((values, concreteTest) => {
      const { concreteTestBreakData, strengthAges, globalId, timeTested } =
        concreteTest
      if (!concreteTestBreakData.length)
        values.push(
          ...strengthAges.map<TestFormItemsValue>((age) => ({
            tempId: crypto.randomUUID(),
            concreteTestId: globalId,
            age,
            breakDate: breakDateByPlacementDate(age),
            timeTested,
          }))
        )
      else
        values.push(
          ...concreteTestBreakData.map<TestFormItemsValue>(
            ({ age, breakDate, labTech, testingLab, __typename, ...rest }) => ({
              ...rest,
              concreteTestId: globalId,
              age: age ?? 28,
              breakDate: toMoment(breakDate),
              labTech: labTechSelectContext.toLabeledValue(labTech),
              testingLab: testingLabSelectContext.toLabeledValue(testingLab),
              timeTested,
            })
          )
        )
      return values
    }, Array<TestFormItemsValue>()),
  }
  const lab = useTestingLabSelectContext()
  const tech = useLabTechSelectContext()
  return (
    <CIMSModal
      visible={visible}
      onCancel={onClose}
      title={`Edit ${testSetId} Break Data`}
      loading={relations.loading || editing}
      disabled={relations.loading || editing}
      width="wide"
      height="tall"
      initialFormValues={initialFormValues}
      onOk={async ({ cores, tests }) => {
        if (!globalId) return errorHandler('Global id must be defined')

        function mapper<T extends BreakDataFormItemsValue>(
          { breakDate, labTech, testingLab, ...rest }: T,
          ..._ignored: any[]
        ) {
          return {
            ...rest,
            breakDate: breakDate?.toISOString(),
            labTechId: tech.fromLabeledValue(labTech),
            testingLabId: lab.fromLabeledValue(testingLab),
          }
        }
        const concreteCoreBreakData = cores?.map(
          ({ placementDate, timeTested, ...rest }) => mapper(rest)
        )
        const concreteTestBreakData = tests?.map(
          ({ tempId: _ignored, placementDate, timeTested, ...rest }) =>
            mapper(rest)
        )
        setEditing(true)
        await edit({
          variables: {
            /**
             * TS correctly complains of required properties from
             * {@link concreteCoreBreakData} items and {@link concreteTestBreakData} items that could be
             * `undefined` or `null`, but we currently rely on server side
             * validation for these properties instead of client validation.
             */
            // @ts-expect-error
            input: { globalId, concreteCoreBreakData, concreteTestBreakData },
          },
        })
        setEditing(false)
        relations.refetch()
        onClose()
      }}
    >
      {!!initialFormValues.cores?.length && (
        <CIMSFormItem
          name="cores"
          customValidator={{
            validator: (datas: CoreFormItemsValue[]) =>
              datas.every(
                ({ age, breakDate }) => typeof age === 'number' && !!breakDate
              ),
            message: 'All break datas must have both Age and Break Date set',
          }}
        >
          <CoreFormItems concretePlacements={concretePlacements} />
        </CIMSFormItem>
      )}
      <CIMSFormItem
        name="tests"
        customValidator={{
          validator: (datas: TestFormItemsValue[]) =>
            datas.every(
              ({ age, breakDate }) => typeof age === 'number' && !!breakDate
            ),
          message: 'All break datas must have both Age and Break Date set',
        }}
      >
        <TestFormItems
          concreteTests={concreteTests}
          placementDate={placementDate ?? null}
        />
      </CIMSFormItem>
    </CIMSModal>
  )
}

export default BreakDataModal
